import { can, type ClientSensitive } from '@selfon/domain/browser';
import { useState } from 'react';
import { api, type ClientRow, type StaffRow } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Chip, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import { won } from '../format';
import { ClientDirectory } from './ClientDirectory';
import { ClientImport } from './ClientImport';

type GoalDraft = { id?: string; text: string; term: 'SHORT' | 'LONG' };
interface FormState {
  displayName: string; birthYear: string; unitPrice: string; therapistIds: string[]; goals: GoalDraft[];
  /** null = 민감정보를 건드리지 않음(기존 값 유지) */
  sensitive: ClientSensitive | null;
}
const EMPTY_SENSITIVE: ClientSensitive = { disabilityType: '', guardianPhone: '', address: '', memo: '' };
const thisYear = new Date().getFullYear();

/** 보호자 동의 현황(읽기 전용). 동의·철회는 보호자가 '우리 아이' 화면에서 한다. */
function ConsentSummary({ centerId, clientId }: { centerId: string; clientId: string }) {
  const q = useLoad(() => api.clientConsents(centerId, clientId), [centerId, clientId]);
  return (
    <fieldset className="consent-summary">
      <legend>보호자 동의</legend>
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !q.data && <p className="hint">불러오는 중…</p>}
      {q.data && (
        <ul className="plain consent-chips">
          {q.data.items.map((i) => (
            <li key={i.type}>
              <Chip tone={i.granted ? 'ok' : 'muted'}>{i.title} · {i.granted ? '동의' : '미동의'}</Chip>
              {i.changedAt && <span className="muted small"> {i.changedAt.slice(0, 10)}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="hint">동의·철회는 보호자가 합니다.</p>
    </fieldset>
  );
}

const kstDateTime = (iso: string) => new Date(Date.parse(iso) + 9 * 3600_000).toISOString().slice(0, 16).replace('T', ' ');

/**
 * 보호자 계정 연결. 센터장이 일회용 코드를 만들어 보호자에게 건네고, 보호자가 '우리 아이'에서 넣는다.
 * 해제하면 이용자 버전이 바뀌므로 onVersion 으로 수정 창의 버전도 맞춘다.
 */
function GuardianLinks({ centerId, clientId, canWrite, onVersion }: { centerId: string; clientId: string; canWrite: boolean; onVersion: (v: number) => void }) {
  const q = useLoad(() => api.clientGuardians(centerId, clientId), [centerId, clientId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const run = async (f: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await f(); q.reload(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const invite = q.data?.invite;
  return (
    <fieldset className="consent-summary guardian-links">
      <legend>보호자 계정 연결</legend>
      {(q.error || error) && <Alert>{q.error ?? error}</Alert>}
      {q.loading && !q.data && <p className="hint">불러오는 중…</p>}
      {q.data && (q.data.guardians.length === 0
        ? <p className="hint">아직 연결된 보호자 계정이 없습니다.</p>
        : <ul className="plain stack">
          {q.data.guardians.map((g) => (
            <li key={g.userId} className="guardian-row">
              <Chip tone="ok">{g.name}</Chip>
              {canWrite && (confirm === g.userId
                ? <span className="row-actions">
                  <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => run(async () => { const r = await api.unlinkGuardian(centerId, clientId, g.userId, q.data!.version); onVersion(r.version); setConfirm(null); })}>해제 확인</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)}>취소</button>
                </span>
                : <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirm(g.userId)} aria-label={`${g.name} 연결 해제`}>연결 해제</button>)}
            </li>
          ))}
        </ul>)}
      {canWrite && q.data && <div className="stack">
        {invite && <p className="invite-code">연결 코드 <strong className="mono" aria-label={`연결 코드 ${invite.code.split('').join(' ')}`}>{invite.code}</strong> <span className="muted small">· {kstDateTime(invite.expiresAt)}까지, 한 번만 쓸 수 있음</span></p>}
        <div><button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => run(async () => { await api.createGuardianInvite(centerId, clientId); })}>{invite ? '새 코드 만들기(이전 코드는 못 씀)' : '연결 코드 만들기'}</button></div>
        <p className="hint">보호자가 ‘우리 아이 → 센터 연결’에 입력합니다.</p>
      </div>}
    </fieldset>
  );
}

/** 이용자 목록과 등록·수정. 민감정보는 일부러 불러와야만 보이고, 불러오면 열람 기록이 남는다. */
export function Clients() {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const clients = useLoad(() => api.clients(centerId), [centerId]);
  const staff = useLoad(() => api.staff(centerId), [centerId]);
  const [editing, setEditing] = useState<ClientRow | 'new' | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loadingSensitive, setLoadingSensitive] = useState(false);
  const [sensitiveError, setSensitiveError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const canWrite = can(p, 'client:write');
  const staffName = new Map((staff.data?.staff ?? []).map((s) => [s.userId, s.name]));
  const activeStaff = (staff.data?.staff ?? []).filter((s) => s.active);

  const open = (c: ClientRow | 'new') => {
    setDone(null); setSensitiveError(null); setEditing(c);
    setForm(c === 'new'
      ? { displayName: '', birthYear: String(thisYear - 6), unitPrice: '55000', therapistIds: [], goals: [], sensitive: { ...EMPTY_SENSITIVE } }
      : { displayName: c.displayName, birthYear: String(c.birthYear), unitPrice: String(c.unitPrice ?? 0), therapistIds: c.therapistIds ?? [], goals: (c.goals ?? []).map((g) => ({ ...g })), sensitive: null });
  };

  const loadSensitive = async () => {
    if (!form || editing === 'new' || !editing) return;
    // 저장된 민감정보가 없으면 서버에서 열람할 것도 없다(불필요한 열람 기록 방지)
    if (!editing.hasSensitive) { setForm({ ...form, sensitive: { ...EMPTY_SENSITIVE } }); return; }
    setLoadingSensitive(true); setSensitiveError(null);
    try {
      const r = await api.sensitive(centerId, editing.id);
      setForm({ ...form, sensitive: r.sensitive ?? { ...EMPTY_SENSITIVE } });
    } catch (e) { setSensitiveError(errorText(e)); } finally { setLoadingSensitive(false); }
  };

  const save = async () => {
    if (!form || !editing) return;
    // 새로 등록하면서 민감정보를 하나도 적지 않았다면 저장하지 않는다
    const blank = form.sensitive && Object.values(form.sensitive).every((v) => !v.trim());
    const sensitive = form.sensitive && !(editing === 'new' && blank) ? form.sensitive : undefined;
    const body = {
      displayName: form.displayName, birthYear: Number(form.birthYear), unitPrice: Number(form.unitPrice),
      therapistIds: form.therapistIds, goals: form.goals.filter((g) => g.text.trim()), sensitive,
    };
    if (editing === 'new') {
      const r = await api.createClient(centerId, body);
      setDone(`${r.client.displayName} 이용자를 등록했습니다.`);
    } else {
      const r = await api.updateClient(centerId, editing.id, { ...body, expectedVersion: editing.version });
      setDone(`${r.client.displayName} 이용자 정보를 저장했습니다.`);
    }
    clients.reload();
  };

  const toggleTherapist = (id: string) => form && setForm({ ...form, therapistIds: form.therapistIds.includes(id) ? form.therapistIds.filter((x) => x !== id) : [...form.therapistIds, id] });
  const setGoal = (i: number, g: Partial<GoalDraft>) => form && setForm({ ...form, goals: form.goals.map((x, j) => (j === i ? { ...x, ...g } : x)) });

  return (
    <>
      <PageHeader title="이용자"
        actions={canWrite && <div className="row"><button type="button" className="btn btn-outline" onClick={() => setImporting(true)}>엑셀로 등록</button><button type="button" className="btn btn-primary" onClick={() => open('new')}>이용자 등록</button></div>} />
      {canWrite && <ClientImport centerId={centerId} staff={staff.data?.staff ?? []} open={importing} onClose={() => setImporting(false)}
        onDone={(n) => { setImporting(false); setDone(`${n}명을 등록했습니다. 보호자는 아동마다 연결 코드로 연결하세요.`); clients.reload(); }} />}
      {done && <Alert kind="ok">{done}</Alert>}
      {clients.error && <Alert>{clients.error}</Alert>}
      {clients.loading && !clients.data && <Loading />}
      {clients.data?.clients.length === 0 && <Empty>{canWrite ? '아직 등록된 이용자가 없습니다. 오른쪽 위 "이용자 등록"으로 시작하세요.' : '담당 이용자가 없습니다.'}</Empty>}
      {clients.data && clients.data.clients.length > 0 && <ClientDirectory clients={clients.data.clients} staffName={staffName} canEdit={canWrite} onEdit={open} />}

      <Dialog wide title={editing === 'new' ? '이용자 등록' : `${editing?.displayName ?? ''} 정보 수정`}
        open={Boolean(editing && form)} onClose={() => { setEditing(null); setForm(null); }} submitLabel={editing === 'new' ? '등록' : '저장'} onSubmit={save}>
        {form && <>
          <div className="form-grid">
            <Field id="c-name" label="이름 (센터 내부 표시용)"><input id="c-name" required maxLength={30} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></Field>
            <Field id="c-year" label="출생연도"><input id="c-year" type="number" required min={thisYear - 40} max={thisYear} value={form.birthYear} onChange={(e) => setForm({ ...form, birthYear: e.target.value })} /></Field>
            <Field id="c-price" label="회기 단가(원)"><input id="c-price" type="number" inputMode="numeric" required min={0} max={1000000} step={1} value={form.unitPrice} onChange={(e) => setForm({ ...form, unitPrice: e.target.value })} /></Field>
          </div>

          <fieldset className="checks">
            <legend>담당 선생님</legend>
            {activeStaff.length === 0 && <p className="hint">등록된 선생님이 없습니다.</p>}
            {activeStaff.map((s: StaffRow) => (
              <label key={s.userId}><input type="checkbox" checked={form.therapistIds.includes(s.userId)} onChange={() => toggleTherapist(s.userId)} /> {s.name} <span className="muted small">{s.title}</span></label>
            ))}
          </fieldset>

          <fieldset className="goals">
            <legend>서비스 목표</legend>
            {form.goals.map((g, i) => (
              <div className="goal-row" key={g.id ?? `new-${i}`}>
                <label className="sr-only" htmlFor={`g-term-${i}`}>목표 {i + 1} 기간</label>
                <select id={`g-term-${i}`} value={g.term} onChange={(e) => setGoal(i, { term: e.target.value as 'SHORT' | 'LONG' })}>
                  <option value="SHORT">단기</option><option value="LONG">장기</option>
                </select>
                <label className="sr-only" htmlFor={`g-text-${i}`}>목표 {i + 1} 내용</label>
                <input id={`g-text-${i}`} maxLength={200} value={g.text} onChange={(e) => setGoal(i, { text: e.target.value })} placeholder="예: 두 낱말 조합으로 요구하기" />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm({ ...form, goals: form.goals.filter((_, j) => j !== i) })} aria-label={`목표 ${i + 1} 삭제`}>삭제</button>
              </div>
            ))}
            {form.goals.length < 10 && <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm({ ...form, goals: [...form.goals, { text: '', term: 'SHORT' }] })}>목표 추가</button>}
          </fieldset>

          <fieldset className="sensitive">
            <legend>민감정보 <span className="muted small">· 암호화해 저장</span></legend>
            {form.sensitive === null ? (
              <div className="stack">
                <p className="hint">불러오면 열람 기록이 남습니다.</p>
                {sensitiveError && <Alert>{sensitiveError}</Alert>}
                <div><button type="button" className="btn btn-outline btn-sm" onClick={loadSensitive} disabled={loadingSensitive || !can(p, 'client:readSensitive')}>
                  {loadingSensitive ? '불러오는 중…' : editing !== 'new' && editing?.hasSensitive ? '민감정보 불러와서 수정' : '민감정보 입력'}
                </button></div>
              </div>
            ) : (
              <div className="form-grid">
                <Field id="s-dis" label="장애유형·진단"><input id="s-dis" maxLength={60} value={form.sensitive.disabilityType} onChange={(e) => setForm({ ...form, sensitive: { ...form.sensitive!, disabilityType: e.target.value } })} /></Field>
                <Field id="s-phone" label="보호자 연락처"><input id="s-phone" type="tel" inputMode="tel" placeholder="010-1234-5678" maxLength={20} value={form.sensitive.guardianPhone} onChange={(e) => setForm({ ...form, sensitive: { ...form.sensitive!, guardianPhone: e.target.value } })} /></Field>
                <Field id="s-addr" label="주소"><input id="s-addr" maxLength={120} value={form.sensitive.address} onChange={(e) => setForm({ ...form, sensitive: { ...form.sensitive!, address: e.target.value } })} /></Field>
                <Field id="s-memo" label="메모"><textarea id="s-memo" rows={2} maxLength={1000} value={form.sensitive.memo} onChange={(e) => setForm({ ...form, sensitive: { ...form.sensitive!, memo: e.target.value } })} /></Field>
              </div>
            )}
          </fieldset>
          {editing !== 'new' && editing && <ConsentSummary centerId={centerId} clientId={editing.id} />}
          {editing !== 'new' && editing && <GuardianLinks centerId={centerId} clientId={editing.id} canWrite={canWrite} onVersion={(v) => setEditing({ ...editing, version: v })} />}
        </>}
      </Dialog>
    </>
  );
}
